import { GroupCategoriesMap } from '@/store/chats/constants'
import type { Chat } from '@/store/chats/IChats'
import type {
  addChats,
  deleteChats,
  setMUChats,
  setPrivateChats,
  setThreadsLoaded,
  updateChat,
  updateChats
} from '@/store/chats/chats'
import { getChatId, getNameOrNick, isMUChat, isPrivateChat } from '@/utils/chats'
import type { MessageEngine } from '@/engine/MessageEngine/MessageEngine'
import type { GetPayload } from '@/engine/interfaces'
import type {
  ChatEngineListeners,
  ChatEngineListenersMap,
  ChatEngineProps, ChatIdMark,
  DividerMark,
  MarkId, NewsMark
} from './ChatEngineTypes'
import { ChatEngineEvents, type ChatEngineEvent } from './constants'
import { isChatIdMark, isNewsMark } from './predicates'
import { getChatId as extractChatIdFromMark, getNewsName, makeChatIdMark } from './utils'
import { CustomChatNames } from '@/constants/CustomChats'
import { getIsDirectRegistration, getUser } from '@/store/user/user'

const FAVORITES_DIVIDER: DividerMark = 'divider:CONTACTS.FAVORITES'
const MY_CHATS_DIVIDER: DividerMark = 'divider:CHAT.MY_CHATS'
const MY_CONTATCS_DIVIDER: DividerMark = 'divider:CONTACTS.MY_CONTACTS'
const MY_SPONSOR_DIVIDER: DividerMark = 'divider:my_sponsor'
const MY_CONSULTANTS_DIVIDER: DividerMark = 'divider:my_consultants'
const MY_CUSTOMERS_DIVIDER: DividerMark = 'divider:my_customers'
const NEWS_MARK: NewsMark = `news:${CustomChatNames.ANNOUNCEMENTS}`
const ALOGIX_MARK: ChatIdMark = `chat:${CustomChatNames.ALOGIX}`

class ChatView<T> {
  private items: readonly T[] = []

  constructor(private readonly compute: () => readonly T[]) {
  }

  recompute(): boolean {
    const next = this.compute()
    if (this.items.length === next.length) {
      let same = true
      for (let i = 0; i < this.items.length; i++) {
        if (this.items[i] !== next[i]) {
          same = false
          break
        }
      }
      if (same) return false
    }
    this.items = next
    return true
  }

  getItems(): readonly T[] {
    return this.items
  }
}

class ChatEngine {
  private readonly getState: ChatEngineProps['getState']
  private readonly messageEngine: MessageEngine
  private readonly chatsCache: ChatEngineProps['chatsCache']
  private readonly listeners: ChatEngineListeners = {}

  private privateChatIds = new Set<string>()
  private muChatIds = new Set<string>()
  private threadsLoaded = false
  private isDirectRegistration = true
  private memberId: string | undefined
  private filter = ''
  private counters = {
    chatsUnread: 0,
    teamUnread: 0,
    invitesIncoming: 0
  }
  private favoritesCount = 0

  private readonly views: {
    chats: ChatView<MarkId>
    contacts: ChatView<MarkId>
    myTeam: ChatView<MarkId>
    invites: ChatView<MarkId>
    allSubscribedChats: ChatView<MarkId>
  }

  private readonly viewEventMap = {
    chats: ChatEngineEvents.Chats,
    contacts: ChatEngineEvents.Contacts,
    myTeam: ChatEngineEvents.MyTeam,
    invites: ChatEngineEvents.Invites,
    allSubscribedChats: ChatEngineEvents.AllSubscribedChats
  } as const

  constructor(props: ChatEngineProps) {
    this.getState = props.getState
    this.messageEngine = props.messageEngine
    this.chatsCache = props.chatsCache
    this.views = {
      chats: new ChatView<MarkId>(() => this.computeChats()),
      contacts: new ChatView<MarkId>(() => this.computeContacts()),
      myTeam: new ChatView<MarkId>(() => this.computeMyTeam()),
      invites: new ChatView<MarkId>(() => this.computeInvites()),
      allSubscribedChats: new ChatView<MarkId>(() => this.computeAllSubscribedChats())
    }
    this.chatsCache.subscribe(this.recompute)
    props.lastMessagesCache.subscribe(this.recompute)
  }

  // Show the cached chat list until the first `/api/user/threads` load completes.
  private get usingCache(): boolean {
    return !this.threadsLoaded && this.chatsCache.hasEntries()
  }

  recompute = () => {
    this.publish()
  }

  subscribe = <K extends ChatEngineEvent>(key: K, listener: ChatEngineListenersMap[K]) => {
    if (!this.listeners[key]) {
      this.listeners[key] = new Set()
    }
    this.listeners[key].add(listener)

    return () => {
      this.listeners[key]?.delete(listener)
    }
  }

  private notify<K extends keyof ChatEngineListenersMap>(
    key: K,
    ...values: Parameters<ChatEngineListenersMap[K]>
  ) {
    this.listeners[key]?.forEach(listener => {
      try {
        listener(...values)
      } catch (error) {
        console.error(error) // eslint-disable-line no-console
      }
    })
  }

  private getChat(chatId: string) {
    if (this.usingCache) {
      return this.chatsCache.getSnapshot(chatId)
    }
    const { chats } = this.getState()
    return chats.privateChats[chatId] || chats.muChats[chatId]
  }

  private getAllIds(): string[] {
    if (this.usingCache) {
      return Object.keys(this.chatsCache.getAll())
    }
    return [...this.privateChatIds, ...this.muChatIds]
  }

  private getPrivateChatIds(): string[] {
    if (this.usingCache) {
      return Object.keys(this.chatsCache.getAll()).filter(id =>
        isPrivateChat(this.chatsCache.getSnapshot(id) ?? id)
      )
    }
    return [...this.privateChatIds]
  }

  private compareByLastMessage = (a: string, b: string) => {
    const t1 = this.messageEngine.getLastMessage(a)?.timestamp || 0
    const t2 = this.messageEngine.getLastMessage(b)?.timestamp || 0
    return t2 - t1
  }

  private compareByName = (a: string, b: string) => {
    const chatA = this.getChat(a)
    const chatB = this.getChat(b)
    const nameA = (chatA && getNameOrNick(chatA)) || ''
    const nameB = (chatB && getNameOrNick(chatB)) || ''
    return nameA > nameB ? 0 : -1
  }

  private compareByLastMessageAndName = (a: string, b: string) => {
    const result = this.compareByLastMessage(a, b)
    return result || this.compareByName(a, b)
  }

  private matchesFilter(chat: Chat): boolean {
    if (!this.filter) return true
    const id = getChatId(chat.$jid).toLowerCase()
    const name = chat.name?.toLowerCase()
    const nick = chat.nick?.toLowerCase()
    return id.includes(this.filter)
      || !!name?.includes(this.filter)
      || !!nick?.includes(this.filter)
  }

  private computeFilteredChats(): MarkId[] {
    if (!this.threadsLoaded) return []
    const matched: string[] = []
    this.getAllIds().forEach(id => {
      const chat = this.getChat(id)
      if (!chat) return
      if (chat.$subscription === 'none') return
      if (!this.matchesFilter(chat)) return
      matched.push(id)
    })
    matched.sort(this.compareByLastMessage)
    return matched.map(makeChatIdMark)
  }

  private computeChats(): MarkId[] {
    this.isDirectRegistration = getIsDirectRegistration(this.getState())
    if (this.filter) {
      return this.computeFilteredChats()
    }
    const out: MarkId[] = []
    if (!this.threadsLoaded && !this.usingCache) {
      return out
    }
    const showCustomChats = !this.isDirectRegistration
    const favorites: string[] = []
    const others: string[] = []
    this.getAllIds().forEach(id => {
      const chat = this.getChat(id)
      if (!chat) return
      if (chat.groups?.includes(GroupCategoriesMap.favorite)) {
        favorites.push(id)
        return
      }
      const groups = chat.groups
      const groupChatAccepted = isMUChat(chat) && chat.$subscription !== 'none'
      const privateChatAccepted = isPrivateChat(chat) && chat.$subscription === 'both' &&
        (!groups || groups.length === 0 || groups.includes(GroupCategoriesMap.accepted))
      if (groupChatAccepted || (privateChatAccepted && this.messageEngine.getLastMessage(id))) {
        others.push(id)
      }
    })
    favorites.sort(this.compareByLastMessageAndName)
    others.sort(this.compareByLastMessageAndName)
    if (showCustomChats || favorites.length > 0) {
      out.push(FAVORITES_DIVIDER)
      if (showCustomChats) {
        out.push(NEWS_MARK, ALOGIX_MARK)
      }
      out.push(...favorites.map(makeChatIdMark))
    }
    if (others.length > 0) {
      out.push(MY_CHATS_DIVIDER, ...others.map(makeChatIdMark))
    }
    return out
  }

  private computeContacts(): MarkId[] {
    if (!this.threadsLoaded && !this.usingCache) {
      return []
    }
    const out: MarkId[] = []
    const favorites: string[] = []
    const others: string[] = []
    this.getPrivateChatIds().forEach(id => {
      const chat = this.getChat(id)
      if (!chat || isMUChat(chat) || chat.$subscription !== 'both') {
        return
      }
      if (chat.groups?.includes(GroupCategoriesMap.favorite)) {
        favorites.push(id)
        return
      }
      if ((chat.groups?.length || 0) > 0 && !chat.groups?.includes(GroupCategoriesMap.accepted)) {
        return
      }
      others.push(id)
    })
    if (favorites.length > 0) {
      favorites.sort(this.compareByName)
      out.push(FAVORITES_DIVIDER, ...favorites.map(makeChatIdMark))
    }
    if (others.length > 0) {
      others.sort(this.compareByName)
      out.push(MY_CONTATCS_DIVIDER, ...others.map(makeChatIdMark))
    }
    return out
  }

  private computeInvites(): MarkId[] {
    if (!this.threadsLoaded && !this.usingCache) return []
    const blockedSet = new Set(
      this.getState().blockList.users.map(u => u.jid)
    )
    const out: string[] = []
    this.getPrivateChatIds().forEach(id => {
      const chat = this.getChat(id)
      if (!chat) return
      if (blockedSet.has(id)) return
      if (chat.$subscription === 'none') return
      const isPending = chat.groups?.includes(GroupCategoriesMap.pending)
      const isFromOnly = chat.$subscription === 'from'
      if (!isPending && !isFromOnly) return
      out.push(id)
    })
    out.sort(this.compareByName)
    return out.map(makeChatIdMark)
  }

  private computeMyTeam(): MarkId[] {
    if (!this.threadsLoaded && !this.usingCache) {
      return []
    }
    const sponsor: string[] = []
    const consultant: string[] = []
    const customer: string[] = []
    this.getPrivateChatIds().forEach(id => {
      const chat = this.getChat(id)
      if (!chat || isMUChat(chat)) {
        return
      }
      if (!this.matchesFilter(chat)) {
        return
      }
      const groups = chat.groups
      if (!groups?.length) {
        return
      }
      if (groups.includes(GroupCategoriesMap.sponsor)) {
        sponsor.push(id)
      }
      if (groups.includes(GroupCategoriesMap.consultant)) {
        consultant.push(id)
      }
      if (groups.includes(GroupCategoriesMap.customer)) {
        customer.push(id)
      }
    })
    sponsor.sort(this.compareByLastMessageAndName)
    consultant.sort(this.compareByLastMessageAndName)
    customer.sort(this.compareByLastMessageAndName)
    const out: MarkId[] = []
    if (sponsor.length) {
      out.push(MY_SPONSOR_DIVIDER, ...sponsor.map(makeChatIdMark))
    }
    if (consultant.length) {
      out.push(MY_CONSULTANTS_DIVIDER, ...consultant.map(makeChatIdMark))
    }
    if (customer.length) {
      out.push(MY_CUSTOMERS_DIVIDER, ...customer.map(makeChatIdMark))
    }
    return out
  }

  private computeAllSubscribedChats(): MarkId[] {
    if (!this.threadsLoaded && !this.usingCache) {
      return []
    }
    const favoriteWith: string[] = []
    const favoriteWithout: string[] = []
    const otherWith: string[] = []
    const otherWithout: string[] = []
    this.getAllIds().forEach(id => {
      const chat = this.getChat(id)
      if (!chat) {
        return
      }
      if (chat.$subscription !== 'both') {
        return
      }
      const hasMessage = !!this.messageEngine.getLastMessage(id)
      const isFavorite = !!chat.groups?.includes(GroupCategoriesMap.favorite)
      if (isFavorite) {
        if (hasMessage) {
          favoriteWith.push(id)
        } else {
          favoriteWithout.push(id)
        }
      } else {
        if (hasMessage) {
          otherWith.push(id)
        } else {
          otherWithout.push(id)
        }
      }
    })
    favoriteWith.sort(this.compareByLastMessage)
    favoriteWithout.sort(this.compareByName)
    otherWith.sort(this.compareByLastMessage)
    otherWithout.sort(this.compareByName)
    return [
      ...favoriteWith,
      ...favoriteWithout,
      ...otherWith,
      ...otherWithout
    ].map(makeChatIdMark)
  }

  private computeFavoritesCount(): number {
    if (!this.threadsLoaded) return 0
    let count = 0
    this.getAllIds().forEach(id => {
      const chat = this.getChat(id)
      if (!chat) return
      if (chat.groups?.includes(GroupCategoriesMap.favorite)) {
        count++
      }
    })
    return count
  }

  private computeCounters() {
    let chatsUnread = 0
    this.views.chats.getItems().forEach(mark => {
      let jid = ''
      if (isChatIdMark(mark)) {
        jid = extractChatIdFromMark(mark)
      } else if (isNewsMark(mark)) {
        jid = getNewsName(mark)
      }
      if (!jid) {
        return
      }
      if (this.messageEngine.getUnreadCount(jid) > 0) {
        chatsUnread++
      }
    })

    const counted = new Set<string>()
    let teamUnread = 0
    this.views.myTeam.getItems().forEach(mark => {
      if (!isChatIdMark(mark)) {
        return
      }
      const jid = extractChatIdFromMark(mark)
      if (counted.has(jid)) {
        return
      }
      counted.add(jid)
      if (this.messageEngine.getUnreadCount(jid) > 0) {
        teamUnread++
      }
    })

    let invitesIncoming = 0
    this.views.invites.getItems().forEach(mark => {
      if (!isChatIdMark(mark)) {
        return
      }
      const chat = this.getChat(extractChatIdFromMark(mark))
      if (chat && !chat.ask) {
        invitesIncoming++
      }
    })

    return { chatsUnread, teamUnread, invitesIncoming }
  }

  private publish() {
    (Object.keys(this.views) as (keyof typeof this.views)[]).forEach(key => {
      if (this.views[key].recompute()) {
        this.notify(this.viewEventMap[key])
      }
    })
    const next = this.computeCounters()
    if (
      next.chatsUnread !== this.counters.chatsUnread
      || next.teamUnread !== this.counters.teamUnread
      || next.invitesIncoming !== this.counters.invitesIncoming
    ) {
      this.counters = next
      this.notify(ChatEngineEvents.Counters)
    }
    const nextFavoritesCount = this.computeFavoritesCount()
    if (nextFavoritesCount !== this.favoritesCount) {
      this.favoritesCount = nextFavoritesCount
      this.notify(ChatEngineEvents.FavoritesCount)
    }
  }

  private syncCategory(target: Set<string>, jids: string[]) {
    target.clear()
    jids.forEach(jid => target.add(jid))
  }

  onClear = () => {
    this.privateChatIds.clear()
    this.muChatIds.clear()
    this.threadsLoaded = false
    this.filter = ''
    this.publish()
  }

  onSetThreadsLoaded = (status: GetPayload<typeof setThreadsLoaded>) => {
    const loaded = status === 'loaded'
    if (this.threadsLoaded === loaded) return
    this.threadsLoaded = loaded
    this.publish()
  }

  setFilter = (value: string) => {
    const normalized = value.trim().toLowerCase()
    if (this.filter === normalized) return
    this.filter = normalized
    this.publish()
  }

  onSetPrivateChats = (chatList: GetPayload<typeof setPrivateChats>) => {
    this.syncCategory(this.privateChatIds, Object.values(chatList).map(c => c.$jid))
    this.publish()
  }

  onSetMUChats = (chatList: GetPayload<typeof setMUChats>) => {
    this.syncCategory(this.muChatIds, Object.values(chatList).map(c => c.$jid))
    this.publish()
  }

  onAddChat = (chat: Chat) => {
    const target = isMUChat(chat) ? this.muChatIds : this.privateChatIds
    if (target.has(chat.$jid)) return
    target.add(chat.$jid)
    this.publish()
  }

  onAddChats = (chatList: GetPayload<typeof addChats>) => {
    let added = false
    Object.values(chatList).forEach(chat => {
      const target = isMUChat(chat) ? this.muChatIds : this.privateChatIds
      if (!target.has(chat.$jid)) {
        target.add(chat.$jid)
        added = true
      }
    })
    if (added) this.publish()
  }

  onDeleteChats = (payload: GetPayload<typeof deleteChats>) => {
    const ids = Array.isArray(payload) ? payload : [payload]
    let deleted = false
    ids.forEach(id => {
      if (this.privateChatIds.delete(id)) deleted = true
      if (this.muChatIds.delete(id)) deleted = true
    })
    if (deleted) this.publish()
  }

  onUpdateChat = (props: GetPayload<typeof updateChat>) => {
    if (this.privateChatIds.has(props.chatJid) || this.muChatIds.has(props.chatJid)) {
      this.publish()
    }
  }

  onUpdateChats = (propsList: GetPayload<typeof updateChats>) => {
    const touched = propsList.some(({ chatJid }) =>
      this.privateChatIds.has(chatJid) || this.muChatIds.has(chatJid)
    )
    if (touched) this.publish()
  }

  onUpdateMessages = () => {
    this.publish()
  }

  onUpdateBlockList = () => {
    this.publish()
  }

  onUpdateUser = () => {
    const memberId = getUser(this.getState())?.memberId
    const isDirectRegistration = getIsDirectRegistration(this.getState())
    if (memberId === this.memberId && isDirectRegistration === this.isDirectRegistration) {
      return
    }
    this.memberId = memberId
    this.publish()
  }

  getChats = (): readonly MarkId[] => this.views.chats.getItems()

  getContacts = (): readonly MarkId[] => this.views.contacts.getItems()

  getMyTeam = (): readonly MarkId[] => this.views.myTeam.getItems()

  getInvites = (): readonly MarkId[] => this.views.invites.getItems()

  getAllSubscribedChats = (): readonly MarkId[] => this.views.allSubscribedChats.getItems()

  getCounters = () => this.counters

  getFavoritesCount = () => this.favoritesCount
}

export { ChatEngine }
